import { notification } from "antd";
import { RcFile } from "antd/es/upload";
import moment from "moment";

export const showNotification = (message: string, type: string) => {
  if (type === "error") {
    notification.error({ message: message });
  }
  if (type === "success") {
    notification.success({ message: message });
  }
  if (type === "warning") {
    notification.warning({ message: message });
  }
};

export const formatDateUtcToCurrentTz = (
  date: string,
  format: string = "YYYY-MM-DD HH:mm"
) => {
  return moment(date + "+00:00").format(format);
};

export const formatDateUtcToCurrent = (
  date: string,
  format: string = "DD MMM'YY hh:mm A"
): string => {
  if (!date || !moment(date).isValid()) {
    return '---'; // fallback for invalid or missing date
  }

  // Parse as UTC, then convert to local time
  console.log("Moment UTC:", moment.utc(date).format());
  return moment.utc(date).local().format(format);
};


export const formatDate = (dateData: any, format: string = "DD-MMM-YYYY") => {
  return moment(dateData).format(format);
};
export const formatDateTime = (
  momentData: any,
  format: string = "YYYY-MM-DDTHH:mm:ssZ"
) => {
  return moment(momentData).format(format);
};
export const passwordValidator = (password: string) => {
  const re =
    /^(?=.*\d)(?=.*[a-z])(?=.*[A-Z])(?=.*[^a-zA-Z0-9])(?!.*\s).{8,20}$/;
  if (re.test(password)) return true;
  return false;
};
export const convertToUTC = (momentData: any) => {
  return moment.utc(momentData).format();
};
export const alphaNumericPattern = /^[\p{Letter}\p{Mark}0-9 ]+$/u;
export const alphaNumericPatternDescription =
  /^[\p{Letter}\p{Mark}0-9 _.,;!"'() ]+$/u;
export const alphabetPattern = /^[\p{Letter}\p{Mark} ]+$/u;

export const capitalizeWords = (data: string) => {
  return data.replace(/(^\w{1})|(\s+\w{1})/g, (letter) => letter.toUpperCase());
};
export const cleanText = (text: string) => {
  return text.split(" ").join("_").toLocaleLowerCase();
};
export const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });

export const getTagColor = (key: string, type: string) => {
  if (type === "rating") {
    return key === "Hot" ? "red" : key === "Warm" ? "orange" : "blue";
  }
  return "purple";
};


export function disabledFutureDates(current: any) {
  return current && current.valueOf() > Date.now();
}

export const formatCost = (value: number) => {
  if (value === null || value === undefined) return "";
  
  return  ("RM " + new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,  // Ensure two decimal points are always displayed
    maximumFractionDigits: 2,  // Limit the number of decimal points to 2
  }).format(value)) ;
};

export const formatCost1 = (value: number, currency: string) => {
  if (value === null || value === undefined) return "";
  
  return  (`${currency} ` + new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,  // Ensure two decimal points are always displayed
    maximumFractionDigits: 2,  // Limit the number of decimal points to 2
  }).format(value)) ;
};

export const formatAmount = (value: string | number) => {
  if (value === null || value === undefined || value === "") return "";

  const num = Number(value);
  if (isNaN(num)) return "";

  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2, // always show 2 decimals
    maximumFractionDigits: 2, // limit to 2 decimals
  }).format(num);
};


export const formatMonthYear = (dateString: string): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  const month = date.toLocaleString('en-US', { month: 'short' }); // e.g. SEP
  const year = date.getFullYear(); // e.g. 2025
  return `${month}-${year}`;
}


export const accessAllowed = (permissions: any, action: string) => {
  return permissions.some((p: any) => p.permission.name === action);
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function base64ToArrayBuffer(base64: string): Uint8Array {
  const binary = window.atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

async function deriveKey(passphrase: string, salt: Uint8Array) {
  const enc = new TextEncoder();
  const keyMaterial = await window.crypto.subtle.importKey(
    "raw",
    enc.encode(passphrase),
    { name: "PBKDF2" },
    false,
    ["deriveKey"]
  );

  return window.crypto.subtle.deriveKey(
    {
      name: "PBKDF2",
      salt,
      iterations: 100000,
      hash: "SHA-256"
    },
    keyMaterial,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export async function encryptCTC(passphrase: string, ctc: string) {
  const salt = window.crypto.getRandomValues(new Uint8Array(16));
  const iv = window.crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const enc = new TextEncoder();

  const ciphertext = await window.crypto.subtle.encrypt(
    { name: "AES-GCM", iv },
    key,
    enc.encode(ctc)
  );

  return {
    encrypted_ctc: arrayBufferToBase64(ciphertext),
    iv: arrayBufferToBase64(iv.buffer),
    salt: arrayBufferToBase64(salt.buffer)
  };
}

export async function decryptCTC(passphrase: string, ctcEncrypted: string, ivB64: string, saltB64: string) {
  const salt = base64ToArrayBuffer(saltB64);
  const iv = base64ToArrayBuffer(ivB64);
  const ciphertext = base64ToArrayBuffer(ctcEncrypted);

  const key = await deriveKey(passphrase, salt);

  const decrypted = await window.crypto.subtle.decrypt(
    { name: "AES-GCM", iv },
    key,
    ciphertext
  );

  return new TextDecoder().decode(decrypted);
}
